import { useMemo } from 'react'
import queryString from 'query-string'
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom'
import { useNavigateContext } from 'src/components/Reusable/Navigation/NavigateContext'
import { FilterParam } from '../../components/Reusable/IndexPage/FilterBar/FilterBar.types'
import {
  GridColumnSortControls,
  GridColumnSortDocuments,
  GridColumnSortEvidence,
  GridColumnSortParams,
  GridColumnSortPolicies,
  GridColumnSortRequests,
  GridColumnSortType,
  GridColumnSortVendors,
  GridColumnSortAccessRequests,
  GridColumnSortAccessPermissions,
  GridColumnSortVisitorAnalytics,
} from '../../components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'

type FilterUrlSearchParams = {
  [key in FilterParam | GridColumnSortParams]?:
    | string
    | GridColumnSortControls[]
    | GridColumnSortPolicies[]
    | GridColumnSortRequests[]
    | GridColumnSortVendors[]
    | GridColumnSortEvidence[]
    | GridColumnSortDocuments[]
    | GridColumnSortType[]
    | GridColumnSortAccessRequests[]
    | GridColumnSortAccessPermissions[]
    | GridColumnSortVisitorAnalytics[]
}

interface UrlSearchParamsHook {
  searchParams: queryString.ParsedQuery
  updateUrlParams: (params: FilterUrlSearchParams) => void
  location: Location
  navigate: NavigateFunction
}

export const useUrlSearchParams = (): UrlSearchParamsHook => {
  const navigateFallback = useNavigate()
  const { navigateState } = useNavigateContext()
  const navigate = navigateState.navigate ?? navigateFallback

  const location = useLocation() as unknown as Location
  const searchParamsFallback = useMemo(() => {
    return queryString.parse(location.search, { arrayFormat: 'bracket' })
  }, [location.search])
  const searchParams = navigateState.parsedQueryParams ?? searchParamsFallback

  const updateUrlParams = (newParams: FilterUrlSearchParams) => {
    const path = queryString.stringifyUrl(
      {
        url: location.pathname,
        query: {
          ...searchParams,
          ...newParams,
        },
      },
      { arrayFormat: 'bracket' },
    )
    navigate(path, { replace: true })
  }

  return {
    searchParams,
    updateUrlParams,
    location,
    navigate,
  }
}
